import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Download } from "lucide-react";
import type { ReportColumn, ResultCell } from "../../../api/portalReportsApi";
import { downloadExport, type ExportFormat } from "../../../lib/dataSheetTransfer";
import { ScrollShadow } from "../../ui/ScrollShadow";
import { columnAlign, columnTotal, formatCell, formatValue } from "./formatCell";

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid var(--portal-border)",
  color: "var(--portal-muted)",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
  cursor: "pointer",
  userSelect: "none",
};

type SortState = { column: string; dir: "asc" | "desc" } | null;

/** Raw-value comparison: numbers/booleans numerically, the rest as text; nulls last. */
function compareCells(a: ResultCell, b: ResultCell): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  const na = typeof a === "number" ? a : Number(a);
  const nb = typeof b === "number" ? b : Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb) && String(a).trim() !== "" && String(b).trim() !== "") return na - nb;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}
const tdStyle: React.CSSProperties = { padding: "6px 10px", borderBottom: "1px solid var(--portal-border)", verticalAlign: "top" };

/**
 * Table section: visible columns in definition order, formatted cells,
 * search across the visible text, click-to-sort headers (on the raw values),
 * client-side paging, a totals row when any column asks for one, and
 * xlsx/csv export of all (filtered, sorted) rows.
 */
export function TableSection({
  title,
  columns,
  colIndex,
  rows,
  pageSize = 100,
  exportName,
}: {
  title: string;
  columns: ReportColumn[];
  colIndex: Record<string, number>;
  rows: ResultCell[][];
  pageSize?: number;
  exportName: string;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>(null);
  const visible = useMemo(() => columns.filter((c) => c.visible !== false && colIndex[c.name] != null), [columns, colIndex]);

  const formatted = useMemo(
    () => rows.map((r) => visible.map((c) => formatCell(r[colIndex[c.name]], c))),
    [rows, visible, colIndex],
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = formatted.map((cells, i) => ({ cells, row: rows[i] }));
    const matched = q ? all.filter((x) => x.cells.some((c) => c.toLowerCase().includes(q))) : all;
    const idx = sort ? colIndex[sort.column] : undefined;
    if (!sort || idx == null) return matched;
    const sign = sort.dir === "asc" ? 1 : -1;
    // Stable: equal keys keep the procedure's order.
    return matched
      .map((x, i) => ({ x, i }))
      .sort((a, b) => compareCells(a.x.row[idx], b.x.row[idx]) * sign || a.i - b.i)
      .map((e) => e.x);
  }, [formatted, rows, search, sort, colIndex]);

  const toggleSort = (column: string) => {
    setSort((s) => (s?.column === column ? (s.dir === "asc" ? { column, dir: "desc" } : null) : { column, dir: "asc" }));
    setPage(1);
  };

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pageCount);
  const slice = filtered.slice((current - 1) * pageSize, current * pageSize);

  const totals = useMemo(() => {
    if (!visible.some((c) => c.total && c.total !== "none")) return null;
    return visible.map((c) => {
      const t = columnTotal(filtered.map((x) => x.row[colIndex[c.name]]), c.total);
      return t == null ? "" : formatValue(t, c.format === "text" ? "decimal" : c.format, c.decimals);
    });
  }, [visible, filtered, colIndex]);

  const doExport = (format: ExportFormat) => {
    downloadExport(format, exportName, visible.map((c) => c.label || c.name), filtered.map((x) => x.cells));
  };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <input
          type="search"
          className="portal-input-inline"
          placeholder={`Search ${title.toLowerCase()}…`}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ padding: "6px 10px", border: "1px solid var(--portal-border)", borderRadius: 6, fontFamily: "inherit", fontSize: "0.9rem", minWidth: 220 }}
        />
        <span style={{ color: "var(--portal-muted)", fontSize: "0.85rem" }}>
          {filtered.length.toLocaleString("en-GB")} {filtered.length === 1 ? "row" : "rows"}
        </span>
        <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
          <button type="button" className="btn btn-secondary" onClick={() => doExport("xlsx")} disabled={filtered.length === 0} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Download size={14} /> Excel
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => doExport("csv")} disabled={filtered.length === 0}>
            CSV
          </button>
        </span>
      </div>
      {/* Same wrapper as the built-in report grids: the table takes its full height and the page scrolls; only horizontal overflow scrolls. */}
      <ScrollShadow>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
          <thead>
            <tr>
              {visible.map((c) => {
                const active = sort?.column === c.name;
                return (
                  <th
                    key={c.name}
                    style={{ ...thStyle, textAlign: columnAlign(c), color: active ? "var(--portal-text)" : thStyle.color }}
                    onClick={() => toggleSort(c.name)}
                    title={active ? (sort?.dir === "asc" ? "Sorted ascending; click for descending" : "Sorted descending; click to clear") : "Click to sort"}
                    aria-sort={active ? (sort?.dir === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {c.label || c.name}
                      {active && (sort?.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {slice.map((x, i) => (
              <tr key={(current - 1) * pageSize + i}>
                {visible.map((c, j) => (
                  <td key={c.name} style={{ ...tdStyle, textAlign: columnAlign(c), whiteSpace: columnAlign(c) === "right" ? "nowrap" : undefined }}>{x.cells[j]}</td>
                ))}
              </tr>
            ))}
            {slice.length === 0 && (
              <tr>
                <td style={{ ...tdStyle, color: "var(--portal-muted)" }} colSpan={Math.max(1, visible.length)}>No rows.</td>
              </tr>
            )}
          </tbody>
          {totals && (
            <tfoot>
              <tr>
                {visible.map((c, j) => (
                  <td key={c.name} style={{ ...tdStyle, textAlign: columnAlign(c), fontWeight: 700, background: "var(--portal-surface)", borderTop: "2px solid var(--portal-border)" }}>
                    {j === 0 && !totals[0] ? "Total" : totals[j]}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </ScrollShadow>
      {pageCount > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "0.85rem" }}>
          <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={current <= 1}>Prev</button>
          <span style={{ color: "var(--portal-muted)" }}>Page {current} of {pageCount}</span>
          <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={current >= pageCount}>Next</button>
        </div>
      )}
    </div>
  );
}
